import { WindowTypeEnum } from '@ben/api/gen/codegen.schemas';
import { parseDateValue } from '@ben/common/date';
import DateInput from '@ben/orion/date-input';
import Select from '@ben/orion/deprecated-select';
import Input from '@ben/orion/input';
import RadioButton from '@ben/orion/radio-button';
import Switch from '@ben/orion/switch';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { StartsOnEnum } from '@ben/shared/types/modular-benefit';
import type { DateInputRangeValue } from '@ben/orion/date-input';
import { TimeInput } from '@mantine/dates';
import { add, format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css, useTheme } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import { startsOnOptions } from '../enrolment-windows/enrolment-starts-on-date';
import { WindowTriggerType } from '../enrolment-windows/enrolment-windows';

const minDate = new Date();
const maxDate = add(new Date(), { years: 20 });

interface ComponentWindowProps {
  componentControllerName: string;
}

const ComponentWindow = ({ componentControllerName }: ComponentWindowProps) => {
  const { control, setValue } = useFormContext();
  const theme = useTheme();

  const firstComponentWindow = `${componentControllerName}.0`;

  const startsOn = useWatch({
    control,
    name: `${firstComponentWindow}.startsOn`,
    exact: true,
  });
  const endsAfterDays = useWatch({
    control,
    name: `${firstComponentWindow}.endsAfterDays`,
    exact: true,
  });
  const startsOnSpecificDate = useWatch({
    control,
    name: `${firstComponentWindow}.startsOnSpecificDate`,
    exact: true,
  });
  const endsOnSpecificDate = useWatch({
    control,
    name: `${firstComponentWindow}.endsOnSpecificDate`,
    exact: true,
  });

  const [showWindow, setShowWindow] = useState(false);

  const [windowTriggerType, setWindowTriggerType] = useState(
    startsOnSpecificDate && endsOnSpecificDate
      ? WindowTriggerType.DATE_RANGE
      : WindowTriggerType.RULE_BASED
  );

  const dateRangeValue =
    !!startsOnSpecificDate && !!endsOnSpecificDate
      ? ([
          parseDateValue(startsOnSpecificDate),
          parseDateValue(endsOnSpecificDate),
        ] as DateInputRangeValue)
      : ([undefined, undefined] as unknown as undefined);

  const isRuleBased = windowTriggerType === WindowTriggerType.RULE_BASED;

  useEffect(() => {
    if (startsOn || !showWindow) return;
    setValue(`${firstComponentWindow}.startsOn`, StartsOnEnum.EMPLOYMENT_START);
    setValue(
      `${firstComponentWindow}.windowType`,
      WindowTypeEnum.CONFIG_WINDOW
    );
  }, [firstComponentWindow, setValue, showWindow, startsOn]);

  useEffect(() => {
    setShowWindow(
      !!startsOn ||
        !!endsAfterDays ||
        !!startsOnSpecificDate ||
        !!endsOnSpecificDate
    );
  }, [endsAfterDays, endsOnSpecificDate, startsOn, startsOnSpecificDate]);

  const handleToggleWindow = (value: boolean) => {
    setShowWindow(value);
    if (!value) {
      setValue(componentControllerName, []);
    }
  };

  const handleDateRangeChange = useCallback(
    ([startDate, endDate]: DateInputRangeValue) => {
      if (startDate && endDate) {
        setValue(
          `${firstComponentWindow}.startsOnSpecificDate`,
          format(startDate, 'yyyy-MM-dd')
        );
        setValue(
          `${firstComponentWindow}.endsOnSpecificDate`,
          format(endDate, 'yyyy-MM-dd')
        );
      }
    },
    [firstComponentWindow, setValue]
  );

  const renderEndsAfterDaysInput = useCallback(
    ({
      field,
    }: {
      field: {
        value: string | number | undefined;
        onChange: (value: string) => void;
        onBlur: () => void;
        ref: React.Ref<HTMLInputElement>;
      };
    }) => (
      <Input
        name="Ends after days"
        ref={field.ref}
        onBlur={field.onBlur}
        onChange={(event) => field.onChange(event.target.value)}
        type="number"
        value={field.value ?? ''}
        disabled={windowTriggerType === WindowTriggerType.DATE_RANGE}
        errorMessage={fieldCannotBeBlankErrorMessage}
        width="240px"
        hintText="Number of days the window is open after the 'Start on' date."
      />
    ),
    [windowTriggerType]
  );

  return (
    <div>
      <Switch
        label="Set a window?"
        defaultValue={showWindow}
        onChange={handleToggleWindow}
      />
      {showWindow && (
        <Section>
          <Typography variant={TypographyVariant.bodyLg2}>
            Choose window type:
          </Typography>
          <RadioButton
            label="Rule based"
            checked={windowTriggerType === WindowTriggerType.RULE_BASED}
            backgroundColor={theme.palette.neutral['00']}
            onChange={() => {
              setWindowTriggerType(WindowTriggerType.RULE_BASED);
              setValue(`${firstComponentWindow}.startsOnSpecificDate`, '');
              setValue(`${firstComponentWindow}.endsOnSpecificDate`, '');
              setValue(`${firstComponentWindow}.startsOnSpecificTime`, null);
              setValue(`${firstComponentWindow}.endsOnSpecificTime`, null);
              setValue(
                `${firstComponentWindow}.startsOn`,
                StartsOnEnum.EMPLOYMENT_START
              );
            }}
          />
          <RowNestedItem>
            <Controller
              name={`${firstComponentWindow}.startsOn`}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={startsOnOptions.filter(
                    (o) => o.key !== StartsOnEnum.BEFORE_ENROLLMENT_END
                  )}
                  disabled={!isRuleBased}
                  name="Starts on"
                />
              )}
            />
            <Controller
              name={`${firstComponentWindow}.endsAfterDays`}
              control={control}
              rules={{
                required: WindowTriggerType.RULE_BASED === windowTriggerType,
              }}
              render={renderEndsAfterDaysInput}
            />
          </RowNestedItem>
          <RadioButton
            label="Date range"
            checked={windowTriggerType === WindowTriggerType.DATE_RANGE}
            backgroundColor={theme.palette.neutral['00']}
            onChange={() => {
              setWindowTriggerType(WindowTriggerType.DATE_RANGE);
              setValue(`${firstComponentWindow}.startsOn`, '');
              setValue(`${firstComponentWindow}.endsAfterDays`, '');
              setValue(
                `${firstComponentWindow}.startsOn`,
                StartsOnEnum.SPECIFIC_DATE
              );
              setValue(
                `${firstComponentWindow}.startsOnSpecificTime`,
                '00:00:00'
              );
              setValue(
                `${firstComponentWindow}.endsOnSpecificTime`,
                '23:59:59'
              );
            }}
          />
          <DateInput<'range'>
            type="range"
            label="Specify date range"
            maxDate={maxDate}
            minDate={minDate}
            disabled={windowTriggerType === WindowTriggerType.RULE_BASED}
            defaultValue={dateRangeValue}
            onChange={handleDateRangeChange}
          />
          <RowNestedItem>
            <Controller
              name={`${firstComponentWindow}.startsOnSpecificTime`}
              control={control}
              render={({ field }) => (
                <TimeInput
                  label="Starts on time"
                  withSeconds
                  w={160}
                  disabled={windowTriggerType === WindowTriggerType.RULE_BASED}
                  value={field.value ?? ''}
                  onChange={(event) => {
                    field.onChange(event.currentTarget.value || null);
                  }}
                />
              )}
            />
            <Controller
              name={`${firstComponentWindow}.endsOnSpecificTime`}
              control={control}
              render={({ field }) => (
                <TimeInput
                  label="Ends on time"
                  withSeconds
                  w={160}
                  disabled={windowTriggerType === WindowTriggerType.RULE_BASED}
                  value={field.value ?? ''}
                  onChange={(event) => {
                    field.onChange(event.currentTarget.value || null);
                  }}
                />
              )}
            />
          </RowNestedItem>
        </Section>
      )}
    </div>
  );
};

export default ComponentWindow;

const Section = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    margin-top: ${theme.spacing(2)};
  `}
`;

const RowNestedItem = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
    margin-top: ${theme.spacing(0.5)};
  `}
`;
